import React, { useCallback, useEffect, useState } from 'react';
import {
  IoCheckmarkCircle,
  IoChevronDownOutline,
  IoCloseCircleOutline,
  IoFlashOutline,
  IoInfiniteOutline,
} from 'react-icons/io5';

import { confirmPendingAction, cancelPendingAction, getPendingAction } from '../atlasApi';
import AppLogo from './AppLogo';
import { useCardDecided } from './cardDecisions';

// A change Atlas wants to make in one of the user's connected apps (Google Drive,
// Docs, Sheets, HubSpot, ...). Nothing has run yet: Approve is the only path that
// runs it (POST /v1/pending-actions/{id}/confirm). "Always allow" also tells the
// backend to run this exact action without a card from now on (removable in
// Settings → Connectors → Permissions on that app). Atlas waits for the click inside its turn, so the
// result flows straight back into the conversation.

const VALUE_CHARS = 280;
const BUTTON_ACTION_CHARS = 34;

function shortLabel(text, max = BUTTON_ACTION_CHARS) {
  const value = String(text || '');
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function shortValue(value) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 1);
  if (!text) return '—';
  return text.length > VALUE_CHARS ? `${text.slice(0, VALUE_CHARS - 1)}…` : text;
}

/** spreadsheet_id → "Spreadsheet id", valueInputOption → "Value input option". */
export function humanizeKey(key) {
  const words = String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

function argumentRows(args) {
  if (!args || typeof args !== 'object') return [];
  return Object.entries(args)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => [humanizeKey(k), shortValue(v)]);
}

export default function AppActionApprovalCard({ approval }) {
  const actionId = approval?.actionId;
  const onDecided = useCardDecided();
  const [status, setStatus] = useState(approval?.status || 'pending');
  const [preview, setPreview] = useState(approval?.preview || {});
  const [result, setResult] = useState(approval?.result || null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [alwaysAllowed, setAlwaysAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!actionId) return undefined;
    getPendingAction({ actionId })
      .then((data) => {
        if (cancelled) return;
        if (data?.preview && typeof data.preview === 'object') setPreview(data.preview);
        if (data?.status && data.status !== 'pending') setStatus(data.status);
        if (data?.result) setResult(data.result);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [actionId]);

  const approve = useCallback(async (always) => {
    if (!actionId || busy) return;
    setBusy(always ? 'always' : 'once');
    setError('');
    try {
      const res = await confirmPendingAction({ actionId, edits: always ? { always: true } : undefined });
      setStatus(res?.status || 'done');
      setResult(res?.result || null);
      if (always) setAlwaysAllowed(true);
      onDecided?.({ actionId, kind: 'app_action', status: res?.status || 'done' });
    } catch (e) {
      setError(e?.message || 'Could not run this change.');
    } finally {
      setBusy('');
    }
  }, [actionId, busy, onDecided]);

  const onCancel = useCallback(async () => {
    if (!actionId || busy) return;
    setBusy('cancel');
    setError('');
    try {
      const res = await cancelPendingAction({ actionId });
      setStatus(res?.status || 'cancelled');
      onDecided?.({ actionId, kind: 'app_action', status: res?.status || 'cancelled' });
    } catch (e) {
      setError(e?.message || 'Could not cancel.');
    } finally {
      setBusy('');
    }
  }, [actionId, busy, onDecided]);

  const app = preview.app || 'connected app';
  const actionName = preview.action || preview.tool_slug || 'this action';
  const what = preview.summary || actionName || 'Make a change';
  const rows = argumentRows(preview.arguments);
  const title = status === 'done'
    ? `Done in ${app}`
    : status === 'failed'
      ? `${app} change failed`
      : status === 'cancelled'
        ? 'Change cancelled'
        : status === 'sending'
          ? `Running in ${app}…`
          : `Approve change in ${app}`;

  return (
    <div className={`atlas-email-approval atlas-email-approval-${status === 'done' ? 'sent' : status} atlas-app-approval`} data-testid="app-action-approval">
      <div className="atlas-email-approval-head">
        <AppLogo toolkit={preview.toolkit} logo={preview.logo} size={15} />
        <span className="atlas-email-approval-title">{title}</span>
      </div>

      <div className="atlas-app-approval-what">{what}</div>
      {preview.summary && preview.action && (
        <div className="atlas-app-approval-action">{app} · {preview.action}</div>
      )}

      {rows.length > 0 && (
        <div className="atlas-app-approval-details">
          <button
            type="button"
            className="atlas-app-approval-details-toggle"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
          >
            <IoChevronDownOutline size={13} className={detailsOpen ? 'is-open' : ''} />
            <span>{detailsOpen ? 'Hide details' : `Details (${rows.length})`}</span>
          </button>
          {detailsOpen && (
            <dl className="atlas-app-approval-fields">
              {rows.map(([key, value]) => (
                <div className="atlas-app-approval-field" key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}

      {status === 'pending' && (
        <div className="atlas-email-approval-actions">
          <button type="button" className="atlas-email-approval-send" onClick={() => approve(false)} disabled={Boolean(busy)}>
            <IoFlashOutline size={15} />
            <span>{busy === 'once' ? 'Running…' : 'Approve once'}</span>
          </button>
          <button
            type="button"
            className="atlas-email-approval-edit atlas-app-approval-always"
            onClick={() => approve(true)}
            disabled={Boolean(busy)}
            aria-label={`Always allow ${actionName} in ${app}`}
            title={`Run “${actionName}” in ${app} without asking from now on. You can turn this off under Permissions next to ${app} in Settings → Connectors.`}
          >
            <IoInfiniteOutline size={15} />
            <span>{busy === 'always' ? 'Running…' : `Always allow “${shortLabel(actionName)}”`}</span>
          </button>
          <button type="button" className="atlas-email-approval-cancel" onClick={onCancel} disabled={Boolean(busy)}>
            <span>Cancel</span>
          </button>
        </div>
      )}

      {status === 'pending' && (
        <div className="atlas-app-approval-scope">
          Always allow covers only <strong>{actionName}</strong> in {app}. Atlas still asks before any other action.
        </div>
      )}

      {status === 'done' && (
        <div className="atlas-email-approval-status atlas-email-approval-status-sent">
          <IoCheckmarkCircle size={15} />
          <span>
            Done in {app}.
            {alwaysAllowed
              ? ` “${actionName}” in ${app} now runs without asking; other actions still ask. Change this under Permissions next to ${app} in Settings → Connectors.`
              : ''}
          </span>
        </div>
      )}

      {status === 'failed' && (
        <div className="atlas-email-approval-status atlas-email-approval-status-failed">
          <IoCloseCircleOutline size={15} />
          <span>{result?.error || 'The app rejected this change.'}</span>
        </div>
      )}

      {status === 'cancelled' && (
        <div className="atlas-email-approval-status">
          <IoCloseCircleOutline size={15} />
          <span>Cancelled — nothing was changed.</span>
        </div>
      )}

      {error && <div className="atlas-email-approval-error">{error}</div>}
    </div>
  );
}
